// pages/CustomerDetail.js
import React, { useState, useMemo, useEffect } from 'react';
import { ContentLayout, PageContainer } from '../components/layout';
import { Card, CardContent, Button, Badge, Loading, ErrorMessage } from '../components/common';
import { AppointmentHistory } from '../components/customer/AppointmentHistory';
import { BatchPaymentSection } from '../components/customer/BatchPaymentSection';
import { CustomerNotes } from '../components/customer/CustomerNotes';
import { CustomerModal } from '../components/modals/CustomerModal';
import { AppointmentModal } from '../components/modals/AppointmentModal';
import { useCustomers } from '../context/CustomerContext';
import { useAppointmentsPG } from '../hooks/useAppointmentsPG';
import { useServicesPG } from '../hooks/useServicesPG';
import { useTranslations } from '../hooks/useTranslations';
import CustomerAppointmentStatusChart from '../components/charts/CustomerAppointmentStatusChart';

export function CustomerDetail({ onNavigate, customerId }) {
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null); // ADD THIS LINE
  const [appointmentsDataLoading, setAppointmentsDataLoading] = useState(true);

  const { customers, loading: customersLoading, error: customersError, handleCustomerSubmit } = useCustomers();
  const { getCustomerAppointments, loading: appointmentsLoading, handleAppointmentSubmit } = useAppointmentsPG();
  const { services } = useServicesPG();
  const t = useTranslations();

  // Find the current customer
  const customer = useMemo(() => {
    return customers.find(c => c.id === customerId);
  }, [customers, customerId]);

  // Get customer's appointments
  const [customerAppointments, setCustomerAppointments] = useState([]);

  useEffect(() => {
    let isMounted = true;

    const fetchAppointments = async () => {
      if (!customerId) {
        setCustomerAppointments([]);
        setAppointmentsDataLoading(false);
        return;
      }

      setAppointmentsDataLoading(true); // START loading
      try {
        const result = await getCustomerAppointments(customerId);
        if (isMounted) setCustomerAppointments(result);
      } catch (err) {
        console.error('Failed to load customer appointments:', err);
        if (isMounted) setCustomerAppointments([]);
      } finally {
        if (isMounted) setAppointmentsDataLoading(false); // END loading
      }
    };

    fetchAppointments();

    return () => {
      isMounted = false;
    };
  }, [customerId, getCustomerAppointments]);


  const handleAppointmentClick = (appointment) => {
    setSelectedAppointment(appointment);
    setIsAppointmentModalOpen(true);
  };

  // Handle edit customer
  const handleEditCustomer = () => {
    setIsCustomerModalOpen(true);
  };

  const handleAppointmentSubmitWithRefresh = async (appointmentData, mode = 'add') => {
    try {
      // Call the original appointment submit handler
      const result = await handleAppointmentSubmit(appointmentData, mode);

      // If successful, refresh the customer appointments
      if (result && result.success !== false) {
        console.log('CustomerDetail: Appointment operation successful, refreshing appointments');

        // Refresh customer appointments
        try {
          const updatedAppointments = await getCustomerAppointments(customerId);
          setCustomerAppointments(updatedAppointments);
        } catch (refreshError) {
          console.warn('CustomerDetail: Failed to refresh appointments after operation:', refreshError);
          // Don't throw - the main operation was successful
        }
      }

      return result;
    } catch (error) {
      console.error('CustomerDetail: Appointment operation failed:', error);
      throw error;
    }
  };

  // Handle book appointment
  const handleBookAppointment = () => {
    setIsAppointmentModalOpen(true);
  };

  // Handle back navigation
  const handleBackToCustomers = () => {
    onNavigate('customers');
  };

  // Loading state - show loading if customers are still loading OR if customer is not found yet
  if (customersLoading || (!customer && customers.length === 0)) {
    return (
      <ContentLayout
        currentPage="customers"
        onNavigate={onNavigate}
        title={t.customerDetails}
        description={t.viewCustomerProfileAndHistory}
      >
        <PageContainer>
          <Loading text={t.loadingCustomerDetails} />
        </PageContainer>
      </ContentLayout>
    );
  }

  // Customer not found - only show error if customers are loaded but customer is not found
  if (!customer && !customersLoading) {
    return (
      <ContentLayout
        currentPage="customers"
        onNavigate={onNavigate}
        title={t.customerDetails}
        description={t.viewCustomerProfileAndHistory}
      >
        <PageContainer>
          <ErrorMessage
            title={t.customerNotFound}
            message={t.requestedCustomerCouldNotBeFound}
          />
          <Button onClick={handleBackToCustomers} className="mt-4">
            {t.backToCustomers}
          </Button>
        </PageContainer>
      </ContentLayout>
    );
  }

  return (
    <ContentLayout
      currentPage="customers"
      onNavigate={onNavigate}
      title={t.customerDetails}
      description={t.viewCustomerProfileAndHistory}
      primaryAction={{
        label: t.bookAppointment,
        onClick: handleBookAppointment,
        icon: <span className="ml-2">📅</span>
      }}
    >
      <PageContainer>
        {/* Back Button */}
        <div className="mb-6">
          <Button
            variant="secondary"
            onClick={handleBackToCustomers}
            className="flex items-center space-x-2"
          >
            <span>←</span>
            <span>{t.backToCustomers}</span>
          </Button>
        </div>

        {/* Error Display */}
        {customersError && (
          <ErrorMessage
            message={customersError}
            className="mb-6"
          />
        )}

        {/* Customer Profile Card */}
        <Card className="mb-8">
          <CardContent>
            <div className="flex flex-col md:flex-row md:items-start md:justify-between space-y-6 md:space-y-0">
              {/* Customer Info */}
              <div className="flex items-start space-x-4">
                {/* Avatar */}
                <div className="w-16 h-16 mx-3 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-green-600 font-semibold text-xl">
                    👤
                  </span>
                </div>

                {/* Basic Info */}
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h1 className="text-2xl font-bold ml-3 text-gray-900">{customer.full_name}</h1>
                  </div>

                  {/* Contact Information */}
                  <div className="space-y-2">
                    <div className="flex items-center text-gray-600">
                      <span className="w-5 ml-3">📞</span>
                      <span>{customer.phone}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <span className="w-5 ml-3">✉️</span>
                      <span>{customer.email}</span>
                    </div>
                    {customer.birth_date && (
                      <div className="flex items-center text-gray-600">
                        <span className="w-5 ml-3">🎂</span>
                        <span>{new Date(customer.birth_date).toLocaleDateString()}</span>
                      </div>
                    )}
                    <div className="flex items-center text-gray-600">
                      <span className="w-5 ml-3">📅</span>
                      <span>{t.customerSince} {new Date(customer.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <div>
                <Button onClick={handleEditCustomer} variant="secondary">
                  {t.editCustomer}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Customer Details Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* Allergies & Notes */}
          <div className="space-y-6">
            {/* Allergies */}
            {customer.allergies && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <span className="text-orange-500 mt-0.5">⚠️</span>
                  <div>
                    <h3 className="font-medium text-red-800 mb-1">{t.allergiesAndSensitivities}</h3>
                    <p className="text-red-700 text-sm">{customer.allergies}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Customer Notes */}
        <CustomerNotes customerId={customerId} />

        <div className="h-8" />

        {/* Customer Appointment Status Chart */}
        <CustomerAppointmentStatusChart
          appointments={customerAppointments}
          loading={appointmentsDataLoading}
        />

        <div className="h-8" />

        {/* Batch Payment Section */}
        {customer && customerAppointments.length > 0 && (
          <>
            <BatchPaymentSection
              appointments={customerAppointments}
              customerId={customerId}
              customerName={customer.full_name}
              customerPhone={customer.phone}
              customerEmail={customer.email}
              onPaymentCreated={(paymentUrl) => {
                console.log('Batch payment link created:', paymentUrl);
                // Optionally refresh appointments after payment created
              }}
            />
            <div className="h-8" />
          </>
        )}

        <div className="h-8" />

        {/* Appointment History */}
        <AppointmentHistory
          appointments={customerAppointments}
          loading={appointmentsDataLoading}
          onAppointmentClick={handleAppointmentClick}
        />


        {/* Customer Modal */}
        <CustomerModal
          isOpen={isCustomerModalOpen}
          onClose={() => setIsCustomerModalOpen(false)}
          onSubmit={handleCustomerSubmit}
          customer={customer}
          mode="edit"
        />

        <div className="h-8" />

        {/* Appointment Modal */}
        <AppointmentModal
          isOpen={isAppointmentModalOpen}
          onClose={() => {
            setIsAppointmentModalOpen(false);
            setSelectedAppointment(null);
          }}
          onSuccess={handleAppointmentSubmitWithRefresh}
          mode={selectedAppointment ? "edit" : "add"}
          appointment={selectedAppointment}
          services={services}
          prefilledCustomerId={selectedAppointment ? null : customer?.id}
        />
      </PageContainer>
    </ContentLayout>
  );
}